import React, { Fragment, useEffect, useState } from "react";
import {
  assoc,
  differenceWith,
  filter,
  flatten,
  includes,
  isEmpty,
  map,
  pipe,
  pluck,
  prop,
  propEq,
  sortBy,
  splitEvery,
  pick,
  toLower,
  reject,
  equals,
} from "ramda";
import { useDispatch, useSelector } from "react-redux";
import {
  COMPLETE_STEP,
  setCoverFile,
  changeWorkFiles,
  updateWorkFileUploadProgress,
  updateWorkFiles,
  WORK_TYPE_FIELD_BLURRED,
  updateCoverFileUploadProgress,
} from "upload/uploadActions";
import {
  changeSubmissionFiles,
  SHOW_CANCEL_SUBMISSION_DIALOG,
  SET_WORK_TYPE_ID,
  SET_ACTIVE_STEP,
  UPDATE_SUBMISSION_FILES,
} from "submission/submissionActions";
import {
  getCoverFile,
  getProjectHideCoverImageUpload,
  getStepErrors,
  getStepLoadingLocalStorage,
  getSubmissionFiles,
  getWorkFiles,
  getWorkType,
  getWorkTypes,
} from "common/selectors";
import { mapUploadFileData } from "../uploadUtils";
import { useRequestUploadUrl, useUploadFile } from "../uploadServices";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";

import FileUploadInput from "general-components/FileUploadInput";
import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlTextField from "../../general-components/IntlTextField";
import LoadingPanel from "general-components/LoadingPanel";
import StepPanelButtonBar from "general-components/StepPanelButtonBar";

import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";

import * as steps from "common/steps";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",

    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },

  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  sectionHeader: {
    marginBottom: theme.spacing(2),
  },

  heading: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.dark,
  },

  subheading: {
    fontSize: ".85rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  uploadSection: {
    marginTop: theme.spacing(3),
  },

  fileUpload: {
    marginBottom: 40,
  },

  label: {
    color: theme.palette.primary.main,
  },
}));

const isUniqueDisplay = (workType, workTypes) =>
  filter(propEq("display", workType.display), workTypes).length === 1;

const findNewFiles = (blobs, files) =>
  differenceWith(
    (blob, file) => blob.name === file.originalFilename,
    blobs,
    files
  );

const getSuccessfulUploads = pipe(
  pluck("value"),
  flatten,
  filter(propEq("status", "fulfilled")),
  pluck("value")
);

const mapInitialWorkFile = file => ({
  blob: file,
  progress: 0,
  status: "loading",
  name: file.name,
});

const Upload = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const submissionFiles = useSelector(getSubmissionFiles);
  const coverFile = useSelector(getCoverFile);
  const errors = useSelector(getStepErrors(steps.FILE_UPLOAD));
  const loading = useSelector(getStepLoadingLocalStorage(steps.FILE_UPLOAD));
  const workFiles = useSelector(getWorkFiles);
  const workType = useSelector(getWorkType);
  const workTypes = useSelector(getWorkTypes);
  const hideCoverImageUpload = useSelector(getProjectHideCoverImageUpload);

  const [showCoverImageUpload, setShowCoverImageUpload] = useState(true);
  useEffect(() => {
    setShowCoverImageUpload(
      !includes(workType.contentTypeName, ["IMAGE"]) && !hideCoverImageUpload
    );
  }, [workType.contentTypeName, hideCoverImageUpload]);

  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  useEffect(() => {
    console.log("WorkTypes...");
    console.log(workTypes);

    const options = pipe(
      workTypes =>
        map(
          workType =>
            isUniqueDisplay(workType, workTypes)
              ? workType
              : assoc(
                  "display",
                  `${workType.display} (${workType.contentTypeDisplay})`,
                  workType
                ),
          workTypes
        ),
      sortBy(prop("display")),
      map(pick(["id", "display", "subTypeName"]))
    )(workTypes);

    console.log("Options...");
    console.log(options);

    setWorkTypeOptions(options);
  }, [workTypes]);

  //-- Handlers
  const setWorkFiles = pipe(changeWorkFiles, dispatch);
  const setSubmissionFiles = pipe(changeSubmissionFiles, dispatch);
  const addNewWorkFiles = pipe(updateWorkFiles, dispatch);
  const updateWorkFile = pipe(updateWorkFileUploadProgress, dispatch);
  const resetFiles = () => {
    setWorkFiles([]);
    setSubmissionFiles([]);
  };

  const setInitialCoverFile = pipe(setCoverFile, dispatch);
  const updateCoverFile = pipe(updateCoverFileUploadProgress, dispatch);

  const onCancel = () =>
    dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true });
  const onNext = () => {
    dispatch({ type: COMPLETE_STEP });
  };
  const onPrevious = () => {
    dispatch({ type: SET_ACTIVE_STEP, step: steps.BASIC_DETAILS });
  };

  const onChangeWorkTypeId = workTypeId => {
    resetFiles();
    dispatch({
      type: SET_WORK_TYPE_ID,
      workTypeId,
    });
  };
  const onWorkTypeFieldBlur = workTypeId => () =>
    dispatch({ type: WORK_TYPE_FIELD_BLURRED, workTypeId });

  const requestUploadUrl = useRequestUploadUrl();
  const uploadFile = useUploadFile();
  const [isUploading, setIsUploading] = useState(false);

  const handleChangeCoverFile = async fileList => {
    if (notBlank(fileList)) {
      setIsUploading(true);
      const fileBlob = fileList[0];
      setInitialCoverFile(mapInitialWorkFile(fileBlob));

      try {
        updateCoverFile(33, "loading");
        const fileData = mapUploadFileData(fileBlob, "COVER");
        const fileUploadResponse = await requestUploadUrl.mutateAsync(fileData);

        updateCoverFile(66, "loading");
        await uploadFile.mutateAsync({
          uploadUrl: fileUploadResponse.uploadUrl,
          fileBlob: fileBlob,
        });

        // Finish Upload
        if (notBlank(fileUploadResponse.fileResource)) {
          updateCoverFile(100, "successful");

          dispatch({
            type: UPDATE_SUBMISSION_FILES,
            fileResources: [fileUploadResponse.fileResource],
          });
        } else {
          throw new Error("An error occurred during upload. Please try again.");
        }
      } catch (err) {
        updateCoverFile(0, "failed");
      }

      setIsUploading(false);
    }
  };

  const handleChangeWorkFiles = async fileBlobs => {
    setIsUploading(true);
    const initialSubmissionFiles = isBlank(submissionFiles)
      ? []
      : submissionFiles;
    const newFiles = map(
      mapInitialWorkFile,
      findNewFiles(fileBlobs, initialSubmissionFiles)
    );

    if (notBlank(newFiles)) {
      if (includes(workType.submissionType, ["ALBUM"])) {
        addNewWorkFiles(newFiles);
      } else {
        setWorkFiles(newFiles);
      }

      const uploads = await Promise.allSettled(
        map(async group => {
          return await Promise.allSettled(
            map(async workFile => {
              try {
                // Get Upload URL
                updateWorkFile(workFile, 33, "loading");
                const fileData = mapUploadFileData(workFile.blob);
                const fileUploadResponse = await requestUploadUrl.mutateAsync(
                  fileData
                );

                // Upload File
                updateWorkFile(workFile, 66, "loading");
                await uploadFile.mutateAsync({
                  uploadUrl: fileUploadResponse.uploadUrl,
                  fileBlob: workFile.blob,
                });

                // Finish Upload
                if (notBlank(fileUploadResponse.fileResource)) {
                  updateWorkFile(workFile, 100, "successful");
                  return fileUploadResponse.fileResource;
                } else {
                  throw new Error(
                    "An error occurred during upload. Please try again."
                  );
                }
              } catch (err) {
                updateWorkFile(workFile, 0, "failed");
                return Promise.reject(err.message);
              }
            }, group)
          );
        }, splitEvery(5, newFiles))
      );

      // Update submission files with new file resources
      const fileResources = getSuccessfulUploads(uploads);
      dispatch({
        type: UPDATE_SUBMISSION_FILES,
        fileResources: fileResources,
      });
    }

    setIsUploading(false);
  };

  const removeCoverFile = coverFile => {
    const updatedSubmissionFiles = reject(
      propEq("originalFilename", coverFile.name),
      submissionFiles
    );

    setInitialCoverFile({});
    setSubmissionFiles(updatedSubmissionFiles);
  };

  const removeWorkFile = workFile => {
    const updatedFiles = reject(propEq("name", workFile.name), workFiles);
    const updatedSubmissionFiles = reject(
      propEq("originalFilename", workFile.name),
      submissionFiles
    );

    setWorkFiles(updatedFiles);
    setSubmissionFiles(updatedSubmissionFiles);
  };

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <div className={classes.container}>
        {Object.keys(workTypes).length > 1 && (
          <Fragment>
            <div className={classes.sectionHeader}>
              <div className={classes.heading}>{t("upload_heading")}</div>
            </div>
            <form autoComplete="none">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <IntlAutocomplete
                    disableClearable
                    autoSelect
                    options={workTypeOptions}
                    value={workType || ""}
                    getOptionLabel={wt =>
                      notBlank(wt)
                        ? t(`upload.work_types.${wt.id}`, wt.display)
                        : ""
                    }
                    getOptionSelected={(opt, val) =>
                      isBlank(val) || opt.id === val.id
                    }
                    onChange={(event, workType) =>
                      onChangeWorkTypeId(workType.id)
                    }
                    renderInput={params => {
                      params.inputProps["aria-autocomplete"] = "none";
                      params.inputProps["id"] = null;
                      return (
                        <IntlTextField
                          {...params}
                          type="text"
                          onBlur={onWorkTypeFieldBlur(workType.id)}
                          error={hasError(errors, "workTypeId")}
                          helperText={t(firstError(errors, "workTypeId"))}
                          name={"workType-" + Math.random()}
                          variant="standard"
                          label={t("upload_work_type")}
                          fullWidth
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Fragment>
        )}
        {!isEmpty(workType) && (
          <Fragment>
            <div className={classes.uploadSection}>
              <form autoComplete="none">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <div className={classes.sectionHeader}>
                      <div className={classes.heading}>
                        {t(
                          "upload_directions",
                          `Upload your ${
                            workType.display === "Other"
                              ? "work"
                              : workType.display.toLowerCase()
                          }`,
                          {
                            type:
                              workType.display === "Other"
                                ? t("upload.work", "work")
                                : toLower(
                                    t(
                                      `upload.work_types.${workType.id}`,
                                      workType.display.toLowerCase()
                                    )
                                  ),
                          }
                        )}
                      </div>
                      <div className={classes.subheading}>
                        {t(
                          includes(workType.submissionType, ["ALBUM"])
                            ? "upload_directions_sub_plural"
                            : "upload_directions_sub"
                        )}
                      </div>
                    </div>
                    <FileUploadInput
                      text={t(
                        includes(workType.submissionType, ["ALBUM"])
                          ? "file_upload_choose_files"
                          : "file_upload_choose_file"
                      )}
                      className={classes.fileUpload}
                      files={workFiles}
                      fileTypes={workType.uploadFileAcceptsDisplay}
                      accept={workType.uploadFileAccepts}
                      acceptMultiple={includes(workType.submissionType, [
                        "ALBUM",
                      ])}
                      onChange={handleChangeWorkFiles}
                      onRemove={removeWorkFile}
                      error={t(firstError(errors, "workFiles"))}
                    />
                  </Grid>

                  {showCoverImageUpload && (
                    <Fragment>
                      <Grid item xs={12} sm={4}>
                        <div className={classes.sectionHeader}>
                          <div className={classes.heading}>
                            {t("upload_cover")}
                          </div>
                          <div className={classes.subheading}>
                            {t(
                              equals(workType.submissionType, "ALBUM")
                                ? "upload_cover_sub_album"
                                : "upload_cover_sub",
                              "Choose the cover image for the album."
                            )}
                          </div>
                        </div>
                        <FileUploadInput
                          text={t("file_upload_choose_file")}
                          className={classes.fileUpload}
                          files={[coverFile]}
                          accept=".jpg, .jpeg, .png, .gif, image/jpeg, image/png, image/gif"
                          acceptMultiple={false}
                          fileTypes={"JPG, PNG or GIF"}
                          onChange={handleChangeCoverFile}
                          onRemove={removeCoverFile}
                          error={t(firstError(errors, "coverFile"))}
                        />
                      </Grid>
                    </Fragment>
                  )}
                </Grid>
              </form>
            </div>
          </Fragment>
        )}
      </div>
      <StepPanelButtonBar
        isUploading={isUploading}
        onNext={onNext}
        onPrevious={onPrevious}
        onCancel={onCancel}
      />
    </Paper>
  );
};

export default Upload;
